import { extractApiErrorMessage } from '@cartwheel/api'
import {
  type CostEstimate,
  clientCostEstimateShowQueryKey,
  type OtherCostEstimateInput,
  useClientCostEstimateCreateMutation,
  useClientCostEstimateUpdateMutation,
} from '@cartwheel/api/v1'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Button } from '../button'
import { TextInput } from '../text-input'

interface OtherEstimateFormProps {
  clientId: string
  estimate: CostEstimate | null
  onSaved: () => void
  onCancel: () => void
}

export function OtherEstimateForm({
  clientId,
  estimate,
  onSaved,
  onCancel,
}: OtherEstimateFormProps) {
  const [customMessage, setCustomMessage] = useState<string>(
    estimate?.editable.custom_message ?? ''
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: clientCostEstimateShowQueryKey({ path: { client_id: clientId } }),
    })

  const createMutation = useClientCostEstimateCreateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })
  const updateMutation = useClientCostEstimateUpdateMutation({
    onSuccess: () => {
      invalidate()
      onSaved()
    },
    onError: (error) =>
      setErrorMessage(extractApiErrorMessage(error, 'Something went wrong. Please try again.')),
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setErrorMessage(null)

    const body: OtherCostEstimateInput = {
      estimate_type: 'other',
      custom_message: customMessage,
    }

    if (estimate?.id) {
      updateMutation.mutate({ path: { client_id: clientId, id: estimate.id }, body })
    } else {
      createMutation.mutate({ path: { client_id: clientId }, body })
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* biome-ignore lint/a11y/noLabelWithoutControl: TextInput renders the <input> */}
      <label className="my-6 grid gap-4">
        <span className="flex justify-between">
          <span className="font-ginto-nord-md text-charcoal text-lg font-semibold">
            Custom message to family
          </span>
        </span>
        <TextInput
          value={customMessage}
          onChange={(event) => setCustomMessage(event.target.value)}
          placeholder="example: This is your custom quote []."
        />
      </label>

      {errorMessage && <div className="mt-2 text-red-700 text-sm text-right">{errorMessage}</div>}

      <div className="flex justify-end gap-2 mt-10">
        <Button type="button" variant="tertiary" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  )
}
